// migrated 1:1 from @nexato/nx-core-module, #688
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    forwardRef,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import moment from 'moment';
import { Overlay } from 'primeng/overlay';
import { Nullable } from 'primeng/ts-helpers';
import { Subscription } from 'rxjs';

export interface FilterDateSelectValue {
    fromDate: string;
    toDate: string;
    includeOverDue?: boolean;
}

/**
 * MultiSelect is used to select multiple items from a collection.
 * @group Components
 */
@Component({
    selector: 'nx-filter-date-select',
    template: `
    <div class="wrapper nx-filter-date-select flex flex-row"
        [ngClass]="{'inputwrapper-filled': isValid()}"
        (click)=toggleOverlay()>
        <div
            *ngIf="!isValid()"
            class="nx-filter-date-select-trigger">
            <div class="nx-filter-date-select-trigger-icon ng-star-inserted">+</div>
        </div>
        <div
            *ngIf="isValid()"
            (click)="clear(); $event.stopPropagation()"
            class="nx-filter-date-select-clear">
            <div class="nx-filter-date-select-clear-icon ng-star-inserted">+</div>
        </div>
        <div class="placeholder">
            {{placeholder}} <ng-container *ngIf="isValid()">:&nbsp;</ng-container>
        </div>
        <div class="value" *ngIf="isValid()">
            {{fromDate | date:'dd.MM.yyyy'}}&nbsp;-&nbsp; 
        </div>
        <div class="value" *ngIf="isValid()">
            {{toDate | date:'dd.MM.yyyy'}}<ng-container *ngIf="includeOverDue !== undefined && includeOverDue">,&nbsp;{{includeOverDueLabel}}</ng-container>
        </div>
        <p-overlay
            #overlay
            [styleClass]="'nx-filter-numberselect'"
            [(visible)]="overlayVisible"
            [target]="'@parent'"
            (onHide)="onHide()"
            [appendTo]="'body'">
            <ng-template pTemplate="content">
                <div class="content">
                    <div class="flex flex-row gap-2">
                        <div class="flex flex-col gap-2">
                        <nx-default-button
                            [style]="'light'"
                            (clicked)="selectToday()"
                            [label]="'core.filterDateSelect.today' | translate">
                        </nx-default-button>
                        <nx-default-button
                            [style]="'light'"
                            (clicked)="selectTomorrow()"
                            [label]="'core.filterDateSelect.tomorrow' | translate">
                        </nx-default-button>
                        <nx-default-button
                            [style]="'light'"
                            (clicked)="selectYesterday()"
                            [label]="'core.filterDateSelect.yesterday' | translate">
                        </nx-default-button>
                        <nx-default-button
                            [style]="'light'"
                            (clicked)="selectCurrentWeek()"
                            [label]="'core.filterDateSelect.currentWeek' | translate">
                        </nx-default-button>
                        <nx-default-button
                            [style]="'light'"
                            (clicked)="selectNextWeek()"
                            [label]="'core.filterDateSelect.nextWeek' | translate">
                        </nx-default-button>
                        </div>
                        <div class="flex flex-col" [formGroup]="fromToFormGroup" *ngIf="fromToFormGroup">
                            <div class="flex flex-row justify-between w-full">
                                <div
                                    class="flex justify-start flex-row text-base mt-1">
                                    <div><span>{{ 'core.filterDateSelect.start' | translate }}:</span><span class="ml-1 mr-4">{{fromDate ? (fromDate | date:'dd.MM.yyyy') : '-'}}</span></div>
                                    <div><span>{{ 'core.filterDateSelect.end' | translate }}:</span><span class="ml-1">{{toDate ? (toDate | date:'dd.MM.yyyy') : '-'}}</span></div>
                                </div>
                                <div class="flex justify-end text-base">
                                <!-- (onChange)="onInputChange($event)"
                                    [(ngModel)]="checked"
                                    [binary]="true"
                                    [disabled]="isDisabled" -->
                                <p-checkbox
                                    [styleClass]="'mr-2'"
                                    *ngIf="fromToFormGroup.get('includeOverDue')"
                                    formControlName="includeOverDue"
                                    [label]="includeOverDueLabel"
                                    [binary]="true"
                                    inputId="binary">
                                </p-checkbox>
                                </div>
                            </div>
                            <div>
                                <p-calendar
                                    formControlName="date"
                                    [numberOfMonths]="2"
                                    selectionMode="range" 
                                    class="max-w-full" 
                                    [inline]="true" 
                                    [showWeek]="true" />                 
                            </div>
                        </div>
                    </div>
                    <div class="flex justify-end">
                        <nx-default-button
                            [style]="'light'"
                            class="ml-2"
                            (clicked)="clear()"
                            [label]="'core.filterDateSelect.clear' | translate">
                        </nx-default-button>
                        <nx-default-button
                            [style]="'accent'"
                            class="ml-2"
                            [disabled]="!isValid()"
                            (clicked)="accept()"
                            [label]="'core.filterDateSelect.apply' | translate">
                        </nx-default-button>
                    </div>
                </div>
            </ng-template>
        </p-overlay>
    </div>
    `,
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => NxFilterDateSelectComponent),
        multi: true
    }],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./nx-filter-date-select.css']
})
export class NxFilterDateSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {

    fromToFormGroup: FormGroup;
    fromDate: Date;
    toDate: Date;
    includeOverDue: boolean;
    @Input() includeOverDueLabel: string;
    @Input() includeOverDuePlaceholder: string;
    accepted = false;
    
    overlayVisible: Nullable<boolean>;
    @ViewChild('overlay') overlayViewChild: Nullable<Overlay>;
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix -- bewusster PrimeNG-API-Mirror, intern (#698)
    @Output() onChange = new EventEmitter<FilterDateSelectValue>();
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix -- bewusster PrimeNG-API-Mirror, intern (#698)
    @Output() onClear = new EventEmitter();
    @Input() placeholder: string;
    _showIncludeOverDue: boolean = false;
    @Input() set showIncludeOverDue(showIncludeOverDue: boolean) {
        if(showIncludeOverDue !== undefined && showIncludeOverDue != this._showIncludeOverDue) {
            this._showIncludeOverDue = showIncludeOverDue;
            this.onShowIncludeOverDueChanged();
        }
    };

    fromToFormGroupSubscription: Subscription;
    ngControl: NgControl;

    private _onChange: (value: FilterDateSelectValue) => void;
    private onTouched: () => void;

    constructor(private _injector: Injector) {
        this.fromToFormGroup = new FormGroup({});
        this.fromToFormGroup.addControl('date', new FormControl());
                this.fromToFormGroup = new FormGroup({});
        this.fromToFormGroup.addControl('date', new FormControl());
        this.fromToFormGroupSubscription =  this.fromToFormGroup.valueChanges.subscribe((value) => {
            if(value?.date && value?.date[0]) {
                this.fromDate = value?.date[0];
            }
            if(value?.date && value?.date[1]) {
                this.toDate = new Date(moment(value?.date[1]).toDate().setHours(23, 59, 59, 999));;
            }
            if(value?.includeOverDue !== undefined) {
                this.includeOverDue = value?.includeOverDue;
            }
        });
    }

    onHide(){
        if(!this.accepted) {
            this.fromToFormGroup.reset();
            this.fromDate = undefined;
            this.toDate = undefined;
            this.includeOverDue = undefined;
        }
        this.accepted = false;
    }

    onShowIncludeOverDueChanged(){
        if(this._showIncludeOverDue) {
            this.fromToFormGroup.addControl('includeOverDue', new FormControl(this.includeOverDue));
        } else {
            this.fromToFormGroup.removeControl('includeOverDue');
        }
    }

    accept() {
        this.accepted = true;
        let value: FilterDateSelectValue = {
            fromDate: this.fromDate ? this.fromDate.toISOString() : undefined,
            toDate: this.toDate ? this.toDate.toISOString() : undefined
        }
        if(this._showIncludeOverDue){
            value.includeOverDue = this.includeOverDue;
        }
        this._onChange(value);
        this.onChange.emit(value);
        this.overlayViewChild.hide();
    }

    isValid() {
        // check the both iso string dates
        return this.fromDate && this.toDate;
    }

    public selectToday() {
        // set to the beginning of the day
        this.fromDate = new Date(moment(new Date()).toDate().setHours(0, 0, 0, 0));
        this.toDate = new Date(moment(new Date()).toDate().setHours(23, 59, 59, 999));
        this.fromToFormGroup.get('date')?.setValue([this.fromDate, this.toDate]);
    }

    public selectTomorrow(){
        this.fromDate = new Date(moment(new Date()).add(1, 'days').toDate().setHours(0, 0, 0, 0));
        this.toDate = new Date(moment(new Date()).add(1, 'days').toDate().setHours(23, 59, 59, 999));
        this.fromToFormGroup.get('date')?.setValue([this.fromDate, this.toDate]);
    }

    public selectYesterday() {
        this.fromDate = new Date(moment(new Date()).subtract(1, 'days').toDate().setHours(0, 0, 0, 0));
        this.toDate = new Date(moment(new Date()).subtract(1, 'days').toDate().setHours(23, 59, 59, 999));
        this.fromToFormGroup.get('date')?.setValue([this.fromDate, this.toDate]);
    }

    public selectCurrentWeek() {
        this.fromDate = new Date(
            moment().startOf('isoWeek').toDate().setHours(0, 0, 0, 0)
        );
        this.toDate = new Date(
            moment().endOf('isoWeek').toDate().setHours(23, 59, 59, 999)
        );
        this.fromToFormGroup.get('date')?.setValue([this.fromDate, this.toDate]);
    }

    public selectNextWeek() {
        // Startzeit auf Montag 00:00:00 der nächsten Woche setzen
        this.fromDate = new Date(
            moment()
                .add(1, 'weeks')
                .startOf('isoWeek')
                .toDate()
                .setHours(0, 0, 0, 0)
        );
        
        // Endzeit auf Sonntag 23:59:59.999 der nächsten Woche setzen
        this.toDate = new Date(
            moment()
                .add(1, 'weeks')
                .endOf('isoWeek')
                .toDate()
                .setHours(23, 59, 59, 999)
        );
        
        this.fromToFormGroup.get('date')?.setValue([this.fromDate, this.toDate]);
    }

    ngOnDestroy(): void {
        this.fromToFormGroupSubscription?.unsubscribe();
    }

    ngOnInit() {
        this.ngControl = this._injector.get(NgControl);
        this.ngControl.valueAccessor = this;
    }

    writeValue(obj: FilterDateSelectValue): void {
        if(obj) {
            this.fromDate = obj.fromDate ? new Date(obj.fromDate) : undefined;
            this.toDate = obj.toDate ? new Date(obj.toDate) : undefined;
            this.includeOverDue = obj.includeOverDue;
            let value: FilterDateSelectValue = {
                fromDate: this.fromDate ? this.fromDate.toISOString() : undefined,
                toDate: this.toDate ? this.toDate.toISOString() : undefined
            }
            if(this._showIncludeOverDue){
                value.includeOverDue = this.includeOverDue;
            }
            this.fromToFormGroup.patchValue(obj,);
            if (this._onChange) {
                this._onChange(value);
            }
        }
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        // currently not supported
    }

    toggleOverlay() {
        if (this.overlayVisible) {
            this.overlayViewChild.hide();
        } else {
            this.overlayViewChild.show();
        }
    }


    clear() {
        this.fromToFormGroup.reset();
        this.fromDate = undefined;
        this.toDate = undefined;
        this._onChange(undefined);
        this.onClear.emit()
    }

}